import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Download, Printer, Loader2, FileText, Building2, Lock, ExternalLink,
} from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function StatementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [statement, setStatement] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, lRes] = await Promise.all([
        accountingAPI.getStatement(id),
        accountingAPI.getStatementLines(id),
      ]);
      setStatement(sRes.data);
      setLines(lRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load statement');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api${accountingAPI.statementPdfUrl(id)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = `${statement.statement_number}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('PDF download failed'); }
  };

  const printPdf = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api${accountingAPI.statementPdfUrl(id)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const b = await r.blob();
      const w = window.open(URL.createObjectURL(b), '_blank');
      if (w) w.addEventListener('load', () => w.print());
    } catch { toast.error('Print failed'); }
  };

  if (loading || !statement) {
    return <div className="p-16 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>;
  }

  const isNegative = Number(statement.net_total) < 0;

  return (
    <div className="page-content" data-testid="statement-detail-page">
      <PageHeader
        title={statement.statement_number}
        description={`${statement.carrier_name} · ${statement.recipient_name} · ${statement.period_label}`}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Accounting', href: '/accounting/carrier-imports' },
          { label: 'Statements', href: '/accounting/statements' },
          { label: statement.statement_number },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadPdf} data-testid="detail-download-btn">
              <Download size={14} className="mr-1" /> Download PDF
            </Button>
            <Button variant="outline" onClick={printPdf} data-testid="detail-print-btn">
              <Printer size={14} className="mr-1" /> Print
            </Button>
            <Button variant="outline" onClick={() => navigate('/accounting/statements')}>
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
          </div>
        }
      />

      {/* Immutable notice */}
      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600 flex items-center gap-2"
           data-testid="immutable-notice">
        <Lock size={12} className="text-slate-400" />
        This statement is <span className="font-semibold text-slate-800">immutable</span>. Corrections must be made through Manual Adjustments (Phase 25.4+).
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Status</div>
          <Badge className={isNegative
              ? 'bg-red-100 text-red-800'
              : 'bg-emerald-100 text-emerald-800'}>
            {statement.status.replace(/_/g, ' ')}
          </Badge>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Total Lines</div>
          <div className="text-2xl font-semibold">{statement.total_lines}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Net Total</div>
          <div className={`text-2xl font-semibold font-mono ${isNegative ? 'text-red-700' : 'text-slate-800'}`}>
            {money(statement.net_total)}
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Remaining Payable</div>
          <div className="text-2xl font-semibold font-mono">{money(statement.remaining_payable_amount)}</div>
        </CardContent></Card>
      </div>

      {/* Recipient + Source Import + Balance */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card><CardContent className="pt-4">
          <div className="text-xs uppercase text-slate-500 mb-1">Recipient</div>
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-slate-400" />
            <div>
              <div className="text-sm font-semibold">{statement.recipient_name}</div>
              <div className="text-[11px] text-slate-500 capitalize">{statement.recipient_type}</div>
            </div>
          </div>
          {statement.recipient_metadata?.email && (
            <div className="mt-2 text-[11px] text-slate-500">{statement.recipient_metadata.email}</div>
          )}
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs uppercase text-slate-500 mb-1">Recipient Balance</div>
          <div className="space-y-1 text-xs">
            <BalanceRow label="Previous"        value={money(statement.recipient_previous_balance)} />
            <BalanceRow label="This Statement"  value={money(statement.net_total)} tone={isNegative ? 'red' : 'emerald'} />
            <div className="border-t border-slate-100 pt-1 mt-1" />
            <BalanceRow label="Current"         value={money(statement.recipient_current_balance)} bold />
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs uppercase text-slate-500 mb-1">Source</div>
          <div className="text-sm">
            <span className="text-slate-500">Carrier:</span> <span className="font-medium">{statement.carrier_name}</span>
          </div>
          <div className="text-sm">
            <span className="text-slate-500">Period:</span> {statement.period_label}
          </div>
          <div className="mt-1">
            <button
              onClick={() => navigate(`/accounting/carrier-imports/${statement.carrier_import_id}`)}
              className="text-xs text-slate-700 hover:underline inline-flex items-center gap-1"
              data-testid="link-to-source-import"
            >
              Carrier Import <ExternalLink size={10} />
            </button>
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Generated {(statement.generated_at || '').slice(0, 19).replace('T', ' ')} · by {statement.generated_by_name}
          </div>
        </CardContent></Card>
      </div>

      {/* Line-item table */}
      <Card>
        <CardContent className="p-0">
          {lines.length === 0 ? (
            <div className="p-10 text-center text-slate-400"><FileText className="mx-auto mb-2" size={20} />No statement lines</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="statement-lines-table">
                <thead>
                  <tr className="bg-slate-50 border-b text-left text-[10px] uppercase text-slate-500">
                    <th className="py-2 px-2">#</th>
                    <th className="py-2 px-2">Producer</th>
                    <th className="py-2 px-2">NPN</th>
                    <th className="py-2 px-2">Product</th>
                    <th className="py-2 px-2">Policy</th>
                    <th className="py-2 px-2">Member</th>
                    <th className="py-2 px-2">Effective</th>
                    <th className="py-2 px-2">Received</th>
                    <th className="py-2 px-2 text-right">KR Received</th>
                    <th className="py-2 px-2 text-right">To Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln) => (
                    <tr key={ln.statement_line_id} className="border-b border-slate-50">
                      <td className="py-1.5 px-2 font-mono text-slate-500">{ln.line_number}</td>
                      <td className="py-1.5 px-2">{ln.producer_name || '—'}</td>
                      <td className="py-1.5 px-2 font-mono">{ln.agent_npn || '—'}</td>
                      <td className="py-1.5 px-2">{ln.product || '—'}</td>
                      <td className="py-1.5 px-2">{ln.policy_number || '—'}</td>
                      <td className="py-1.5 px-2">{ln.member_name || '—'}</td>
                      <td className="py-1.5 px-2">{ln.effective_date || '—'}</td>
                      <td className="py-1.5 px-2">{ln.received_date || '—'}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{money(ln.amount_received_by_kr)}</td>
                      <td className={`py-1.5 px-2 text-right font-mono font-semibold ${
                          ln.amount_to_pay < 0 ? 'text-red-700' : 'text-emerald-700'
                        }`}>{money(ln.amount_to_pay)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                    <td colSpan={8} className="py-2 px-2 text-right text-slate-600">Positive:</td>
                    <td colSpan={2} className="py-2 px-2 text-right font-mono text-emerald-700">{money(statement.positive_amount)}</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td colSpan={8} className="py-1.5 px-2 text-right text-slate-600">Chargebacks:</td>
                    <td colSpan={2} className="py-1.5 px-2 text-right font-mono text-red-700">{money(statement.negative_amount)}</td>
                  </tr>
                  <tr className="bg-slate-100 border-t-2 border-slate-300 font-bold">
                    <td colSpan={8} className="py-2 px-2 text-right">Net Statement Total:</td>
                    <td colSpan={2} className={`py-2 px-2 text-right font-mono ${isNegative ? 'text-red-700' : 'text-slate-900'}`}>{money(statement.net_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BalanceRow({ label, value, tone, bold }) {
  const cls = tone === 'red' ? 'text-red-700'
            : tone === 'emerald' ? 'text-emerald-700'
            : 'text-slate-800';
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`font-mono ${bold ? 'font-semibold' : ''} ${cls}`}>{value}</span>
    </div>
  );
}
